"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/ui/components/primitives/card";
import { Badge } from "@/ui/components/primitives/badge";
import { Monitor, Server, Zap, Clock } from "lucide-react";
import type { SystemHealth } from "@/services/api/services/admin-dashboard";
import { getWebhookSuccessRate, safeNumber } from "@/services/api/services/admin-dashboard";

interface SystemHealthMetricsProps {
  data: SystemHealth;
}

export function SystemHealthMetrics({ data }: SystemHealthMetricsProps) {
  // Use the utility function for webhook success rate calculation
  const webhookSuccessRate = getWebhookSuccessRate(data.webhookStatus);

  const getStatusBadge = (rate: number) => {
    if (rate >= 95) return { text: "Healthy", variant: "default" as const };
    if (rate >= 85) return { text: "Warning", variant: "secondary" as const };
    return { text: "Critical", variant: "destructive" as const };
  };

  const webhookStatus = getStatusBadge(webhookSuccessRate);
  const apiStatus = getStatusBadge(100 - safeNumber(data.apiPerformance.errorRate));

  // Add null safety for PSP providers
  const primaryProvider = data.pspHealth?.providers?.[0];
  const pspStatus = primaryProvider?.status === "HEALTHY"
    ? { text: "Healthy", variant: "default" as const }
    : primaryProvider?.status === "DEGRADED"
    ? { text: "Degraded", variant: "secondary" as const }
    : primaryProvider?.status === "DOWN"
    ? { text: "Down", variant: "destructive" as const }
    : { text: "Unknown", variant: "secondary" as const };

  return (
    <div className="admin-grid admin-grid-3">
      {/* Webhook Health */}
      <Card className="admin-card-plain">
        <CardHeader className="admin-card-header">
          <CardTitle className="admin-card-title flex items-center gap-2">
            <Monitor className="h-4 w-4" />
            System Health
          </CardTitle>
        </CardHeader>
        <CardContent className="admin-card-body">
          <div style={{ marginBottom: '10px' }}>
            <div className="admin-text-xs admin-text-muted">Webhook delivery</div>
            <div className="admin-text-sm" style={{ marginTop: '2px' }}>
              {safeNumber(data.webhookStatus.successfulDeliveries)} / {safeNumber(data.webhookStatus.totalWebhooks)} successful
            </div>
            <div style={{ marginTop: '6px' }}>
              <div
                className="admin-progress"
                style={{ '--value': `${webhookSuccessRate}%` } as React.CSSProperties}
              >
                <div className="admin-progress-inner"></div>
              </div>
            </div>
          </div>

          <div style={{ height: '1px', background: 'var(--border)', margin: '8px 0' }} />

          <div style={{ marginBottom: '10px' }}>
            <div className="admin-text-xs admin-text-muted">PSP provider</div>
            <div className="admin-text-sm" style={{ marginTop: '4px', display: 'flex', gap: '6px', alignItems: 'center' }}>
              <span style={{ fontWeight: 500 }}>{primaryProvider?.name?.toUpperCase() || 'UNKNOWN'}</span>
              <Badge className={`admin-badge-${pspStatus.variant === 'default' ? 'success' : pspStatus.variant === 'secondary' ? 'warning' : 'danger'}`}>
                {pspStatus.text} · {safeNumber(primaryProvider?.uptime || 99.8)}% uptime
              </Badge>
            </div>
          </div>

          <div style={{ height: '1px', background: 'var(--border)', margin: '8px 0' }} />

          <div>
            <div className="admin-text-xs admin-text-muted">Avg webhook delivery time</div>
            <div className="admin-text-sm" style={{ marginTop: '4px' }}>
              {safeNumber(data.webhookStatus.averageDeliveryTime)} ms
            </div>
          </div>
        </CardContent>
      </Card>

      {/* API Performance */}
      <Card className="admin-card-plain">
        <CardHeader className="admin-card-header">
          <CardTitle className="admin-card-title flex items-center gap-2">
            <Zap className="h-4 w-4" />
            API Performance
          </CardTitle>
        </CardHeader>
        <CardContent className="admin-card-body">
          <div className="grid" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '8px' }}>
            <div>
              <div className="admin-text-xs admin-text-muted">Avg response time</div>
              <div className="admin-text-sm" style={{ marginTop: '2px', fontWeight: 600 }}>
                {safeNumber(data.apiPerformance.averageResponseTime)} ms
              </div>
              <div className="admin-text-xs admin-text-muted">Target &lt; 200 ms</div>
            </div>
            <div>
              <div className="admin-text-xs admin-text-muted">Requests / second</div>
              <div className="admin-text-sm" style={{ marginTop: '2px', fontWeight: 600 }}>
                {safeNumber(data.apiPerformance.requestsPerSecond)}
              </div>
              <div className="admin-text-xs admin-text-muted">Live throughput</div>
            </div>
            <div>
              <div className="admin-text-xs admin-text-muted">Error rate</div>
              <div className="admin-text-sm" style={{ marginTop: '2px', fontWeight: 600 }}>
                {safeNumber(data.apiPerformance.errorRate)}%
              </div>
              <div style={{ marginTop: '4px' }}>
                <div
                  className="admin-progress"
                  style={{ '--value': `${safeNumber(data.apiPerformance.errorRate)}%` } as React.CSSProperties}
                >
                  <div className="admin-progress-inner" style={{
                    background: 'linear-gradient(to right, var(--admin-danger), var(--admin-warning))'
                  }}></div>
                </div>
              </div>
            </div>
            <div>
              <div className="admin-text-xs admin-text-muted">System status</div>
              <div style={{ marginTop: '4px' }}>
                <Badge className={`admin-badge-${apiStatus.variant === 'default' ? 'success' : apiStatus.variant === 'secondary' ? 'warning' : 'danger'}`}>
                  {apiStatus.text}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Additional Metrics */}
      <Card className="admin-card-plain">
        <CardHeader className="admin-card-header">
          <CardTitle className="admin-card-title flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Service Status
          </CardTitle>
        </CardHeader>
        <CardContent className="admin-card-body">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="admin-text-xs admin-text-muted">Database</span>
              <Badge className="admin-badge-success">Connected</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="admin-text-xs admin-text-muted">Redis Cache</span>
              <Badge className="admin-badge-success">Operational</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="admin-text-xs admin-text-muted">Payment Gateway</span>
              <Badge className="admin-badge-success">Healthy</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="admin-text-xs admin-text-muted">Email Service</span>
              <Badge className="admin-badge-success">Active</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="admin-text-xs admin-text-muted">Storage</span>
              <Badge className="admin-badge-soft">Normal</Badge>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}